/**
 * Formatters shared by the Trailblazers statistics cards.
 *
 * Deliberately local rather than reusing `formatDuration` from
 * shared-react-hooks: that one takes SECONDS and renders 0 as "Unlimited",
 * which is right for a round's time limit and actively wrong for a measured
 * duration, where 0 means zero.
 */

/** A duration in ms as "1h 04m", "12m 34s" or "45s". Null renders as an em dash. */
export function formatMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
}

/** A UTC ms instant as "HH:MM:SS" — the games are single-evening, so no date. */
export function formatClock(utcMs: number | null | undefined): string {
  if (utcMs == null) return '—';
  return new Date(utcMs).toISOString().slice(11, 19);
}

/** Elapsed time from the game's start, as "+12m 34s". */
export function formatOffset(utcMs: number, startedAtUtcMs: number | null): string {
  if (startedAtUtcMs == null) return formatClock(utcMs);
  return `+${formatMs(utcMs - startedAtUtcMs)}`;
}

/** Ordinal placement: 1 → "1st". */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Sabotage/event type slugs as readable text: `treasure_thief` → "Treasure Thief". */
export function humanizeSlug(slug: string): string {
  return slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
